import React, { useState, useEffect, useRef } from "react";
import { useMutation, useSubscription } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

import StatusBadge from "designV2/components/StatusBadge";
import Button from "designV2/components/Button";
import DialogShell from "designV2/components/DialogShell";
import FunOrderMap from "./FunOrderMap";
import RejectOrder from "./RejectOrder";
import PrepareOrder from "./PrepareOrder";
import DeliveryOrder from "./DeliveryOrder";
import "./orderModals.css";

const REQUEST_SPARK_DELIVERY = gql`
  mutation reCreateOrderDeliveryByShopAdmin($orderId: ID!) {
    reCreateOrderDeliveryByShopAdmin(orderId: $orderId) {
      _id
      status
    }
  }
`;

const UPDATE_ORDERS_SUBSCRIPTION = gql`
  subscription updateOrder($orderId: ID!) {
    updateOrder(orderId: $orderId) {
      _id
      user {
        _id
      }
      shipmentModel
      type
      finalPrice
      commission
      createdAt
      finished
      status
      trip {
        _id
        trackId
        origin {
          coordinates
        }
      }
      tracking {
        trackId
      }
      cart {
        products {
          quantity
          product {
            _id
            title
          }
        }
      }
    }
  }
`;

const SEND_NOTIFICATION_TO_SINGLE_DRIVER_OR_USER = gql`
  mutation createNotificationByShopAdmin(
    $driver: ID
    $user: ID
    $title: String!
    $body: String!
    $type: NotificationType!
    $for: ForInput!
  ) {
    createNotificationByShopAdmin(
      input: { user: $user, driver: $driver, title: $title, body: $body, type: $type, for: $for }
    ) {
      _id
      body
    }
  }
`;

const GET_TRIP_BY_ID = gql`
  mutation getOrderTripById($orderId: ID!) {
    getOrderTripById(orderId: $orderId) {
      _id
      createdAt
    }
  }
`;

const statusLabel = (status) => (status ? status.replace(/_/g, " ").toLowerCase() : "-");

/**
 * DeliveryStatus - manage an in-progress order: reject it, request a
 * Spark delivery, mark it prepared, notify the customer, or hand it to
 * a driver. Content-only, rendered inside a DialogShell by Orders.js.
 * Reject/Prepare/Deliver each open their own nested DialogShell.
 */
const DeliveryStatus = ({ order: item, close, refetch }) => {
  const [rejectModal, setRejectModal] = useState(false);
  const [preparedModal, setPreparedModal] = useState(false);
  const [deliveryModal, setDeliveryModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [secsToNextSparkDelivery, setSecsToNextSparkDelivery] = useState(-1);
  const [isLoadingNextSparkDeliveryTime, setIsLoadingNextSparkDeliveryTime] = useState(false);
  const [isRequestingSparkDelivery, setIsRequestingSparkDelivery] = useState(false);

  const [createNotificationByShopAdmin] = useMutation(SEND_NOTIFICATION_TO_SINGLE_DRIVER_OR_USER);

  const [order, setOrder] = useState(item);

  const { data: subUpdateData } = useSubscription(UPDATE_ORDERS_SUBSCRIPTION, {
    variables: { orderId: order._id },
  });

  useEffect(() => {
    if (subUpdateData) setOrder(subUpdateData.updateOrder);
  }, [subUpdateData]);

  const [reCreateOrderDeliveryByShopAdmin] = useMutation(REQUEST_SPARK_DELIVERY);
  const [getOrderTripById] = useMutation(GET_TRIP_BY_ID);

  const intervalRef = useRef(null);

  useEffect(() => {
    if (order.trip) {
      setIsLoadingNextSparkDeliveryTime(true);
      getOrderTripById({ variables: { orderId: order._id } })
        .then((res) => {
          const now = new Date();
          const tripTime = new Date(res.data.getOrderTripById.createdAt);
          const timeDiff = now - tripTime - 15000;
          if (5 * 60 * 1000 - timeDiff > 0) {
            setSecsToNextSparkDelivery(5 * 60 * 1000 - timeDiff);
            let loop = 1;
            intervalRef.current = setInterval(() => {
              setSecsToNextSparkDelivery((prevValue) => {
                const newValue = prevValue - 1000;
                if (loop !== 1 && newValue <= -1) {
                  clearInterval(intervalRef.current);
                }
                return newValue;
              });
              loop += 1;
            }, 1000);
          }
          setIsLoadingNextSparkDeliveryTime(false);
        })
        .catch(() => setIsLoadingNextSparkDeliveryTime(false));
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAndStop = () => {
    clearInterval(intervalRef.current);
    close();
  };

  const onRequestSendNotification = () => {
    createNotificationByShopAdmin({
      variables: {
        title: "Your order is ready!",
        body: `Your order from ${order.shop.name}, is ready.`,
        type: "IMPORTANT",
        user: order.user._id.trim(),
        for: "USER",
      },
    });
  };

  const onRequestSparkDelivery = () => {
    setIsRequestingSparkDelivery(true);
    reCreateOrderDeliveryByShopAdmin({ variables: { orderId: order._id } })
      .then(() => {
        clearInterval(intervalRef.current);
        close();
        refetch();
        setIsRequestingSparkDelivery(false);
      })
      .catch((err) => {
        console.log(err);
        setIsRequestingSparkDelivery(false);
      });
  };

  return (
    <div className="orderModalBody orderModalBodyWide">
      <div className="orderDetailsTopRow">
        <StatusBadge status="info" label={statusLabel(order.status)} />
      </div>

      {order.status === "SHIPPING" && order.trip && order.trip.origin && (
        <button type="button" className="orderModalMapToggle" onClick={() => setShowMap((v) => !v)}>
          {showMap ? "Hide map" : "Show map"}
          {showMap ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </button>
      )}

      {showMap && (
        <div className="orderModalMap">
          <FunOrderMap
            trackId={order.tracking.trackId}
            destination={{
              lat: order.trip.destinations && order.trip.destinations[0].coordinates[1],
              lng: order.trip.destinations && order.trip.destinations[0].coordinates[0],
            }}
            origin={{
              lat: order.trip.origin.coordinates[1],
              lng: order.trip.origin.coordinates[0],
            }}
            position={{
              lat: order.trip.origin.coordinates[1],
              lng: order.trip.origin.coordinates[0],
            }}
          />
        </div>
      )}

      <div className="orderModalActions">
        {(order.status === "DELIVERY_NOT_ACCEPTED" || order.status === "PAID" || order.status === "PREPARING") && (
          <Button variant="danger" onClick={() => setRejectModal(true)}>
            Reject order
          </Button>
        )}

        {isLoadingNextSparkDeliveryTime ? (
          <Button disabled>Loading...</Button>
        ) : !order.trip || secsToNextSparkDelivery < 0 ? (
          <Button
            variant="primary"
            onClick={() => !isRequestingSparkDelivery && onRequestSparkDelivery()}
            loading={isRequestingSparkDelivery}
          >
            Request Spark delivery
          </Button>
        ) : (
          <Button disabled>
            Spark delivery available in {Math.floor(secsToNextSparkDelivery / 1000)}s
          </Button>
        )}

        {order.status === "PREPARING" && (
          <Button variant="primary" onClick={() => setPreparedModal(true)}>
            Mark prepared
          </Button>
        )}
        {order.status === "PREPARED" && (
          <Button onClick={onRequestSendNotification}>Send notification</Button>
        )}
        {order.status === "PREPARED" && (
          <Button variant="primary" onClick={() => setDeliveryModal(true)}>
            Mark delivered
          </Button>
        )}
      </div>

      <DialogShell open={rejectModal} onClose={() => setRejectModal(false)} title="Reject order" size="sm">
        <RejectOrder
          close={() => {
            setRejectModal(false);
            closeAndStop();
            refetch();
          }}
          orderId={order._id}
        />
      </DialogShell>

      <DialogShell open={preparedModal} onClose={() => setPreparedModal(false)} title="Mark as prepared" size="sm">
        <PrepareOrder
          close={() => {
            setPreparedModal(false);
            closeAndStop();
            refetch();
          }}
          orderId={order._id}
        />
      </DialogShell>

      <DialogShell open={deliveryModal} onClose={() => setDeliveryModal(false)} title="Mark as delivered" size="sm">
        <DeliveryOrder
          close={() => {
            setDeliveryModal(false);
            closeAndStop();
            refetch();
          }}
          orderId={order._id}
        />
      </DialogShell>
    </div>
  );
};

export default DeliveryStatus;
